import { createServerFn } from '@tanstack/react-start'
import { getRequest, setResponseHeader } from '@tanstack/react-start/server'

import {
  VISITOR_COOKIE,
  cookieHeader,
  getOrCreateCookieId,
} from './cookies'
import { findProposalBySlug } from './db'
import type { ProposalRow } from './db'
import { captureServer, isBot } from './posthog'
import type { PublicProfile } from './landingTypes'

const VISITOR_MAX_AGE = 60 * 60 * 24 * 365

export const getProfileBySlug = createServerFn({ method: 'GET' })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest()
    const proposal = await findProposalBySlug(data.slug)
    if (!proposal) return null

    setResponseHeader('Cache-Control', 'private, no-store')
    setResponseHeader('Vary', 'Cookie')

    const visitorId = getOrCreateCookieId(request, VISITOR_COOKIE)
    setResponseHeader(
      'Set-Cookie',
      cookieHeader(VISITOR_COOKIE, visitorId, VISITOR_MAX_AGE),
    )

    await recordOpen(request, proposal, visitorId)

    return toPublicProfile(proposal)
  })

/**
 * Authoritative server-side "brief opened" signal. PostHog's webhook
 * destination forwards `brief_opened` to the external service on every open
 * (the `surface` and `isBot` properties let it filter as needed).
 */
async function recordOpen(
  request: Request,
  proposal: ProposalRow,
  visitorId: string,
) {
  const userAgent = request.headers.get('User-Agent') ?? ''
  await captureServer({
    event: 'brief_opened',
    distinctId: visitorId,
    properties: {
      proposalId: proposal.id,
      slug: proposal.slug,
      externalProposalId: proposal.external_proposal_id,
      surface: 'brief',
      referrer: request.headers.get('Referer'),
      isBot: isBot(userAgent),
    },
  })
}

function toPublicProfile(proposal: ProposalRow): PublicProfile {
  return {
    proposalId: proposal.id,
    slug: proposal.slug,
    externalProposalId: proposal.external_proposal_id,
    config: JSON.parse(proposal.landing_config_json),
  }
}
